import { Url, IUrl, Singleton } from "app/utils";
import {
  Application,
  $Constants,
  $CurrentUser,
  $Router,
  stringify,
} from "app/classes";
import { $IdentityResource, $SalesforceResource } from "app/resources";
import {
  IdentityService,
  $RavenService,
  $LaunchDarklyService,
} from "app/services";
import { angular, app } from "app/ng/ng.module";
import { SETTINGS } from "./settings"
import { fromObject } from "url-env";
import auth0 from "auth0-js";
import axios from "axios";
import {renderFlags} from "./react/SpsFlags/index";

const publicAppPathsWithoutSlashes = ($Constants?.apps ?? []).filter(app => app.public).map(app => '/' + app.slug);
const publicAppPaths = publicAppPathsWithoutSlashes.concat(publicAppPathsWithoutSlashes.map(path => path + '/'), ['/auth/reset-password', '/auth/reset-password/', '/auth/invalid-invite', '/auth/invalid-invite/', '/auth/create-new-password', '/auth/create-new-password/', '/auth/create', '/auth/create/'])
const publicPaths = ['/auth0/callback', '/auth0/callback/', '/auth0/login', '/auth0/login/', '/auth/login', '/auth/login/'].concat(publicAppPaths);

// This is just a function that returns a promise. 
// It will be replaced by a fetch call once there is an identity
// endpoint that makes the decision on whether a browser's list 
// of orgs allow it to use auth0
export async function doesIdentitySayBrowserIsAuth0Capable ({organizations = [], identityPrefix = 'dev.'}) {
    const url = `https://${identityPrefix}id.spsc.io/identity/v1/capabilities/use-auth0-login/check/?organizations=${organizations.join(',')}`
    try {
      const res = await axios.get(url);
      return res.data.enabled_count > 0;
    } catch(err) {
      return false;
    }
}

export async function checkToken ({identityPrefix = 'dev.', token}) {
  const url = `https://${identityPrefix}id.spsc.io/identity/token/check/`
  try {
    const res = await axios.post(url, undefined, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    return res.status === 200;
  } catch(err) {
    return false;
  }
}

// Org hint queues are just a list of org ids that
// a given browser has logged into previously. 
// This function takes an array, adds a new org
// to it and returns a new list. It pushes one org
// out of the queue if the queue has reached a max
// length.
function createOrgHintQueue(oldOrgIds, newOrgId) {
  // It already exists in the queue so just return the queue
  if (oldOrgIds.indexOf(newOrgId) > -1) {
    return oldOrgIds;
  }
  const newQueue = [...oldOrgIds];
  if (oldOrgIds.length === 4) {
    newQueue.shift();
  }
  newQueue.push(newOrgId);
  return newQueue;
}

// Saves org hints to localstorage so that we can know
// which orgs this client tends to use
function persistOrgHintQueueToLocalStorage(queueToPersist) {
  localStorage.setItem("orgHint", JSON.stringify(queueToPersist));
  return queueToPersist;
}

// Reads org hints that the client has previously used
function readOrgHintQueueFromLocalStorage() {
  try {
    return JSON.parse(localStorage.getItem("orgHint")) || [];
  } catch (e) {
    console.warn('Error parsing orgHint json', e);
   return [];
  }
}

// This function determines if we should use identity or auth0
// based on a series of localstorage values, some network requests
// from identity
async function getAuthCapabilityResult (previousAccessToken, auth0Env) {

  const {identityPrefix} = auth0Env;

  // Check if auth0 is explicitly disabled for this browser
  const localStorageAllowsAuth0 = localStorage.getItem('disableAuth0') !== "yes";

  // Check if always use auth0 is explicitly set. This is lower priority to disableAuth0
  const alwaysUseAuth0 = localStorage.getItem('alwaysUseIdentity') !== "yes" && !window['ARE_WE_TESTING'] === true;

  // This does a request to identity to see if we have been included in
  // the auth0 capability.
  const identityAllowsAuth0 = await doesIdentitySayBrowserIsAuth0Capable({organizations: readOrgHintQueueFromLocalStorage(), identityPrefix});
  // This is the final stage of making the decision
  const shouldUseAuth0 = (identityAllowsAuth0 && localStorageAllowsAuth0) || (alwaysUseAuth0 && localStorageAllowsAuth0)

  //const shouldUseAuth0 = (identityAllowsAuth0 && (localStorageAllowsAuth0 || alwaysUseAuth0));
  const tokenSource = shouldUseAuth0 || previousAccessToken? "auth0" : "identity";
  const token = (previousAccessToken) ? previousAccessToken : (alwaysUseAuth0 ? undefined : $CurrentUser.token );
  return {
    token,
    tokenSource,
    shouldUseAuth0,
    alwaysUseAuth0
  }
}

// This method is responsible for parsing the url and caling
// auth0's authorize endpoint which finds the correct url
// to redirect to.
function parseAndAuthorizeAuth0(auth0ClientInstance, apps, pathsToFilterOut, auth0Env) {
  const initialFullUrl =  $Router.savedRoute.fullpath;
  let initialFullParsed;
  try {
    initialFullParsed = new URL(initialFullUrl);
  } catch (err) {
    // if it isn't a url we don't need to do anything because initialFullParsed
    // can just be undefined.
  }
  
  // initialFullParsed?.pathname
  const initialPath = initialFullParsed?.pathname;
  const initialSearch = initialFullParsed?.search;

  // Filter out any paths we don't want used as initial path
  const filteredInitialPath = pathsToFilterOut.includes(initialPath) ? undefined : initialPath; 
  // use auth0 instead of cp login 
  auth0ClientInstance.authorize({
      realm: apps[auth0Env.internal].realm,
      appState: stringify({
        initialPath: filteredInitialPath ?? '/launchpad/',
        initialSearch: initialSearch ?? ''
      }),
      redirectUri : url + '/auth0/callback'
  });
  return;
}

type Auth0ParseResult = {
  expiresIn: number
  accessToken: string
  appState: any
}
function parseHash (args): Promise<Auth0ParseResult> {
  return new Promise((resolve, reject) => {
    auth.parseHash(args, (err, authResult) => {
      if(err) {
        reject(err);
      } else {
        resolve(authResult);
      }
    })
  });
}

// This method, when  called gets the environment from the url
// and returns an object that represents internal and external
// environments. 
// An internal environment is what commerce platform uses and
// includes the option that it is local since we perform certain
// actions in local conditions. Externally, however, do do not
// want the fact that we are running locally to be leaked.
// This is because many apps calling into CP's message bus will
// not know what to do with 'local' and will break.
// We define this split brain here as a way to be able to run 
// CP locally but with apps that are not broken. 
function getEnvironment () {
  try {
    return fromObject({
      'https://localhost:9300': {internal: 'local', external: "dev", identityPrefix: "dev."},
      'https://dev.commerce.spscommerce.com': {internal: 'dev', external: "dev", identityPrefix: "dev."},
      'https://test.commerce.spscommerce.com': {internal: 'test', external: "test", identityPrefix: "test."},
      'https://stage.commerce.spscommerce.com': {internal: 'stage', external: "stage", identityPrefix: "stage."},
      'https://commerce.spscommerce.com': {internal: 'prod', external: "prod", identityPrefix: ""}
    });
  } catch (e) {
    return {
      url: 'https://localhost:9300',
      env: {internal: 'local', external: "dev", identityPrefix: 'dev.'}
    }
  }
}


// We always try to load the previous token because otherwise
// the user would be logged out just because they refreshed the
// page
const previousAccessToken = localStorage.getItem('access_token') ?? undefined;


// Turn urls into environments
const {url, env: auth0Env} = getEnvironment();

// Internally we know
const identityEnv = {
  internal: $Constants?.env,
  external: !$Constants || $Constants?.env === "local" ? "dev" : $Constants.env,
}

// This is our auth0 config. All of these are apps in the
// prod auth0 tennant
const apps = SETTINGS.AUTH0_CLIENTS;

export { auth0Env, apps, url };

// Create an instance of auth0-js using the correct environment
const auth = new auth0.WebAuth({
  ...apps[auth0Env.internal],
  audience: "api://api.spscommerce.com/",
  responseType: "token",
  scope: 'openid profile email',
});

export class CommercePlatform {
  public ngLoaded = false;

  public async init() {

    const startUp = ({tokenSource = 'identity', token, identityEnv, auth0Env, initialPath = `/launchpad/`, initialSearch = ''}) => {

      // The token source determines which of the environments we are going to use
      const env = tokenSource === 'auth0' ? auth0Env : identityEnv;

      // We pass in the split environment so that we can use it to respond to 'local' in one
      // application event while replying as 'dev' to the other application event.
      // We set this before starting session because startSession because it is async and
      // and we want to prime the pump for the first render.
      $Router.setEnvironment(env);

      // This resource takes an actual resolved environment
      $IdentityResource.setEnvironment(env.external);

      this.registerPublicApps();
      // Passing in the hardcoded launchpad app
      // that we want to use at the top of the app
      // switcher.

      $CurrentUser
        .startSession({hardcodedLaunchpad: $Constants.hardcodedLaunchpad, env, tokenSource, token})
        .catch(() => {}) // noop for no session
        .finally(() => {
          if (!$CurrentUser.buyOnlineComplete) {
            // If the user has not finished the buy online app, do not allow them to navigate away
            console.log('skipping routing to keep user in buy-online application');
            return;
          }
        $Router.gotoSavedRoute();
        });

      $CurrentUser.onLogin(() => {
        persistOrgHintQueueToLocalStorage(
          createOrgHintQueue(readOrgHintQueueFromLocalStorage(), $CurrentUser.company.id)
        )
        this.loadAngularApp();
        $RavenService.setupUser($CurrentUser);
        $CurrentUser.apps.forEach($Router.registerApp);
        $Router.broadcast("userAuthenticated", { token });
        if (!$CurrentUser.buyOnlineComplete) {
          console.log('Directing to buy-online app');
          $Router.goto('/buy-online/');
        } else {
          $Router.goto(initialPath + initialSearch);
        }
      });

      $CurrentUser.onLogout(async (logoutProps) => {
        const wasTokenStored = Boolean(localStorage.getItem('access_token'));
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('lang');
        localStorage.removeItem('access_token');
        localStorage.removeItem('expires_at');
        localStorage.removeItem('lang');
        $Router.broadcast("userUnauthenticated");
        $Router.unregisterApps();
        $LaunchDarklyService.logoutUser();
        // If the user does not have the correct org token, we want to retain
        // her savedRoute so that we can send her to it once she has the
        // correct org token.
        if ($CurrentUser.hasCorrectOrgToken() && !logoutProps?.switchToOrgId) {
          $Router.saveRoute();
        }

        // Determines if the url contains an org_id query param
        const currentUrl = Url.getCurrent();
        const currentUrlObj = Url.parse(currentUrl);
        const searchObj = Url.parseSearch(currentUrlObj.search);
        const orgId = searchObj["cp.orgid"];
        const orgId_queryParam = orgId ? `&cp.orgid=${orgId}` : "";
        const logoutUrl = `https://${apps[auth0Env.internal].domain}/v2/logout?client_id=${apps[auth0Env.internal].clientID}&returnTo=${window.location.protocol}//${window.location.host}`;

        if(wasTokenStored) {
          window.location.href = logoutUrl;
          return;
        }
        const nextAuth0CapabilityResult = await getAuthCapabilityResult(previousAccessToken, auth0Env);
        const { shouldUseAuth0 } = nextAuth0CapabilityResult; 

        if(logoutProps?.switchUsingAuth0) {
          localStorage.setItem('triedToSwitchToOrgId', logoutProps?.switchToOrgId);
          // Create an instance of auth0-js using the correct environment
          const auth = new auth0.WebAuth({ 
            ...apps[auth0Env.internal],
            audience: "api://api.spscommerce.com/",
            responseType: "token",
            scope: 'openid profile email', 
          });
          // fire le redirect
          auth.authorize({
            realm: apps[auth0Env.internal].realm,
            appState: stringify({
              initialPath: '/launchpad/'
            }),
            login_hint: logoutProps?.switchToOrgId,
            redirectUri : url + '/auth0/callback'
          });
          return;
        }

        if(window['ARE_WE_TESTING']) {
          $Router.goto(`auth/login?${orgId_queryParam}`);
          return;
        } else {
          window.location.href = logoutUrl;
          return;
        }
      });
    }
    if(window.location.search.indexOf("showcommerceplatformflags") > -1) {
      await renderFlags();
    }

    const { token, tokenSource, shouldUseAuth0 } = await getAuthCapabilityResult(previousAccessToken, auth0Env);
    
    if((window.location.pathname === "/auth0/callback" || window.location.pathname === "/auth0/callback/")) {
        const triedToSwitchToOrgId = localStorage.getItem('triedToSwitchToOrgId') ?? undefined;
        // Handle redirects back into the app from auth0
        // We can't have this branch with the other calls
        // to auth0 since it exists inside a callback
        try {
          const parseResult = await parseHash({ hash: window.location.hash });
          if (parseResult && parseResult.accessToken) {
            localStorage.removeItem('triedToSwitchToOrgId');
            const expiresAt = JSON.stringify(
              parseResult.expiresIn * 1000 + new Date().getTime()
            );

            let appState;

            try {
              appState = JSON.parse(parseResult.appState);
            } catch (err) {
              appState = {}
            }

            const initialPath = appState?.initialPath ?? '/launchpad/';
            const initialSearch = appState?.initialSearch ?? '';
            // TODO: deal with refreshes
            //this.scheduleRenewal();
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('lang');
            localStorage.setItem('access_token', parseResult.accessToken);
            localStorage.setItem('expires_at', expiresAt);
            startUp({tokenSource: "auth0", token: parseResult.accessToken, auth0Env, identityEnv, initialPath, initialSearch});

          } else {
            if(!triedToSwitchToOrgId) {
              // Create an instance of auth0-js using the correct environment
              const auth = new auth0.WebAuth({ 
                ...apps[auth0Env.internal],
                audience: "api://api.spscommerce.com/",
                responseType: "token",
                scope: 'openid profile email', 
              });
              // fire le redirect
              auth.authorize({
                realm: apps[auth0Env.internal].realm,
                appState: stringify({
                  initialPath: '/launchpad/'
                }),
                login_hint: triedToSwitchToOrgId,
                redirectUri : url + '/auth0/callback',
                prompt: 'none'
              });
            }
          }
        } catch (err) {
          if(err.error === "login_required") {
            const auth = new auth0.WebAuth({ 
              ...apps[auth0Env.internal],
              audience: "api://api.spscommerce.com/",
              responseType: "token",
              scope: 'openid profile email', 
            });
            // fire le redirect
            auth.authorize({
              realm: apps[auth0Env.internal].realm,
              appState: stringify({
                initialPath: '/launchpad/'
              }),
              login_hint: triedToSwitchToOrgId,
              redirectUri : url + '/auth0/callback'
            });
          } else {
            console.error(err);
            //window.history.back();
            throw new Error('Error parsing hash after callback: ' + err.error + ' Error decription: ' + err.description);
          }
        }
      return;
    } else {
      // Not dealing with an auth0 callback
      const isPublicApp = publicAppPaths.includes(window.location.pathname);
      if(isPublicApp) {
        startUp({tokenSource, token, auth0Env, identityEnv});
        return;
      }
      const freshToken = await checkToken({identityPrefix: auth0Env.identityPrefix, token});
      // Redirect to auth0 login
      if((!token && shouldUseAuth0) || (!freshToken && (window.location.pathname === '/auth/login/' || window.location.pathname === '/auth/login') ) || (window.location.pathname === '/auth0/login/' || window.location.pathname === '/auth0/login')) {
        return parseAndAuthorizeAuth0(auth, apps, publicPaths, auth0Env);
      }

    }

    const freshToken = await checkToken({identityPrefix: auth0Env.identityPrefix, token});

    if(!freshToken && !window['ARE_WE_TESTING']) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('expires_at');
      return parseAndAuthorizeAuth0(auth, apps, publicPaths, auth0Env);
    }
    // Priority:
    //   - previously saved token
    //   - alwaysUseAuth0
    //   - identity
    startUp({tokenSource, token, auth0Env, identityEnv});
    return;
  }

  /**
   * Load the Angular portion of our application. This is deferred until the
   * user is logged in to save on load times.
   */
  public loadAngularApp(): void {
    if (!this.ngLoaded) {
      const bluebar = document.getElementById("bluebar");
      angular.bootstrap(bluebar, [app]);
      this.ngLoaded = true;
    }
  }

  private registerPublicApps() {
    const apps = $Constants.apps || [];
    apps.forEach((appDefinition) => {
      $Router.registerApp(new Application(appDefinition));
    });
  }
}

export let $CommercePlatform = Singleton("$CommercePlatform", CommercePlatform);
